/*!glb-color-select.js
  
  Legge lo stato dei colori da variabili globali (snapshot o core palette)
  e scrive una rappresentazione testuale/HTML dei colori in un elemento target della pagina.

  Funzioni esposte:
  - ROOT.init(target, userOpts)
    * Inizializza il modulo con il target (selector o elemento) e opzioni utente.
    * Avvia polling, watcher snapshot e reapply su mutation se configurati.
    * Restituisce un oggetto con metodi: update, start, stop, getOptions, onChange.
  - ROOT.update()
    * Forza la lettura dei colori e scrive il contenuto nel target configurato.
    * Restituisce un oggetto { written: boolean, count: number }.
  - ROOT.onChange(cb)
    * Registra un callback che viene chiamato quando il contenuto viene scritto con successo.
    * Restituisce una funzione per rimuovere il callback registrato.
  - ROOT.cleanup()
    * Ferma polling, observer e watcher; pulisce callback e riferimento globale.

  Note implementative:
   - Legge da window.__GLB_COLOR_STATE_SNAPSHOT e window.__GLB_COLOR_CORE__.
*/

(function () {
  "use strict";

  const ROOT = (window.GLBColorSelect = window.GLBColorSelect || {});

  const DEFAULTS = {
    target: null,
    pollInterval: 0,
    maxPollAttempts: 50,
    preferSnapshot: true,
    reapplyOnMutation: true,
    reapplyAttempts: 3,
    reapplyDebounceMs: 80,
    snapshotPollInterval: 300,
    snapshotCompareByTs: true,
    locale: null,
    pollBackoffBase: 2,
    pollBackoffMaxMs: 5000,
    pollInitialDelayMs: 200,
    forceSyncForInputs: true
  };

  const TITLE_MAP = { it: "Colore:", en: "Colors:", fr: "Couleurs:", de: "Farben:", es: "Colores:" };

  let opts = Object.assign({}, DEFAULTS);
  let targetEl = null;
  let pollTimer = null;
  let pollCount = 0;
  let changeCallbacks = [];
  let mutationObserver = null;
  let reapplyCount = 0;
  let snapshotTimer = null;
  let lastSnapshotKey = null;
  const tmpDiv = document.createElement("div");
  let pendingWrite = false;
  let lastWriteArgs = null;

  const safeRun = (fn, fallback) => {
    try { return fn(); } catch (e) { return typeof fallback !== "undefined" ? fallback : null; }
  };

  const safe = v => (v == null ? "" : String(v));

  // Restituisce il codice lingua (es. "it") determinato da opzioni, document o browser
  function getLangCode(locale) {
    return safeRun(() => {
      const docLang = document.documentElement && document.documentElement.lang ? document.documentElement.lang : null;
      const shopifyLang = window.Shopify && (Shopify.locale || Shopify.shop_locale) ? (Shopify.locale || Shopify.shop_locale) : null;
      const raw = locale || opts.locale || docLang || shopifyLang || navigator.language || "it";
      return String(raw).replace("_", "-").split("-")[0].toLowerCase();
    }, "it");
  }

  // Titolo localizzato per la sezione colori
  function localizedTitle() {
    const lang = getLangCode(null);
    return TITLE_MAP[lang] || TITLE_MAP.it;
  }

  // Escape HTML per evitare injection quando si costruisce innerHTML
  function escapeHtml(s) {
    return safe(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  // Legge le selezioni dallo snapshot globale e le normalizza in array {label, hex}
  function readSnapshotSelections() {
    return safeRun(() => {
      const s = window.__GLB_COLOR_STATE_SNAPSHOT;
      if (!s || !Array.isArray(s.selections)) return [];
      const out = new Array(s.selections.length);
      for (let i = 0; i < s.selections.length; i++) {
        const sel = s.selections[i];
        const c = sel && sel.color ? sel.color : {};
        out[i] = { label: safe(c.label || c.colorName || c.name), hex: safe(c.hex || c.colorHex) };
      }
      return out;
    }, []);
  }

  // Legge la palette core globale e la normalizza in array {label, hex}
  function readCorePalette() {
    return safeRun(() => {
      const core = window.__GLB_COLOR_CORE__ && window.__GLB_COLOR_CORE__.colorCache;
      if (!core || !Array.isArray(core.colors)) return [];
      const out = new Array(core.colors.length);
      for (let i = 0; i < core.colors.length; i++) {
        const c = core.colors[i];
        out[i] = { label: safe(c.label || c.name), hex: safe(c.hex || c.colorHex) };
      }
      return out;
    }, []);
  }

  // Risolve quale sorgente usare (snapshot o core) in base alle opzioni
  function resolveColors() {
    const snap = readSnapshotSelections();
    if (opts.preferSnapshot && snap.length) return snap;
    const core = readCorePalette();
    if (!opts.preferSnapshot && core.length) return core;
    return snap.length ? snap : core;
  }

  // Formatta la rappresentazione testuale semplice dei colori
  function formatPlain(colors) {
    const title = localizedTitle();
    if (!colors || !colors.length) return title + "\n";
    const lines = new Array(colors.length);
    for (let i = 0; i < colors.length; i++) {
      const c = colors[i];
      const hex = c.hex ? ` ${c.hex}` : "";
      const label = c.label || c.hex || "";
      lines[i] = `${i + 1}:${hex} ${label}`;
    }
    return `${title}\n` + lines.join("\n");
  }

  // Converte HTML in testo semplice rimuovendo tag
  function stripHtmlToText(html) {
    tmpDiv.innerHTML = html || "";
    return tmpDiv.textContent || tmpDiv.innerText || "";
  }

  // Costruisce una rappresentazione HTML dei colori (cerchietti + label)
  function formatHTML(colors) {
    const title = `<div style="margin-bottom:10px;font-weight:600;">${escapeHtml(localizedTitle())}</div>`;
    if (!colors || !colors.length) return title;
    const parts = new Array(colors.length);
    for (let i = 0; i < colors.length; i++) {
      const c = colors[i];
      const label = escapeHtml(c.label || c.hex || "");
      const hex = (c.hex && c.hex.trim()) ? c.hex.trim() : "";
      const bg = hex || "#ffffff";
      const circle = `<span title="${escapeHtml((c.label ? c.label + " " : "") + (hex || ""))}" style="display:inline-block;width:20px;height:20px;border:1px solid #dddddd;border-radius:50%;background:${escapeHtml(bg)};flex:0 0 20px;margin-right:8px;"></span>`;
      parts[i] = `<div style="display:flex;align-items:center;line-height:24px;white-space:nowrap;margin:2px 0;"><span style="flex:0 0 auto;margin-right:6px;">${i + 1}:</span>${circle}<span style="vertical-align:middle;">${label}</span></div>`;
    }
    return title + parts.join("");
  }

  // Pianifica la scrittura sul target; per input/textarea forza sincronizzazione e dispatch di eventi
  function scheduleWrite(el, plain, html) {
    lastWriteArgs = { el, plain, html };
    const tag = (el && el.tagName || "").toUpperCase();

    if (opts.forceSyncForInputs && (tag === "INPUT" || tag === "TEXTAREA")) {
      if (el.value !== plain) {
        el.value = plain;
        try { el.dispatchEvent(new Event("input", { bubbles: true })); } catch (e) {}
        try { el.dispatchEvent(new CustomEvent("glb:input", { bubbles: true, detail: { value: plain } })); } catch (e) {}
      }
      pendingWrite = false;
      return;
    }

    if (pendingWrite) return;
    pendingWrite = true;

    let rafId = null;
    let timeoutId = null;

    function doWrite() {
      pendingWrite = false;
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      if (timeoutId) { clearTimeout(timeoutId); timeoutId = null; }

      const args = lastWriteArgs;
      lastWriteArgs = null;
      const tagInner = (args.el.tagName || "").toUpperCase();

      if (tagInner === "INPUT" || tagInner === "TEXTAREA") {
        if (args.el.value !== args.plain) {
          args.el.value = args.plain;
          try { args.el.dispatchEvent(new Event("input", { bubbles: true })); } catch (e) {}
          try { args.el.dispatchEvent(new CustomEvent("glb:input", { bubbles: true, detail: { value: args.plain } })); } catch (e) {}
        }
      } else {
        const useInnerHTML = !!(args.html && args.html !== args.plain);
        const desired = useInnerHTML ? args.html : args.plain;
        if (useInnerHTML) {
          if (args.el.innerHTML !== desired) args.el.innerHTML = desired;
        } else {
          if (args.el.textContent !== desired) args.el.textContent = desired;
        }
      }
    }

    try {
      rafId = requestAnimationFrame(doWrite);
    } catch (e) {
      timeoutId = setTimeout(doWrite, 0);
      return;
    }

    timeoutId = setTimeout(() => { if (pendingWrite) doWrite(); }, 120);
  }

  // Scrive effettivamente sul target; ritorna true se la scrittura è stata pianificata o non necessaria
  function writeToTarget(el, plain, html) {
    if (!el) return false;
    const tag = (el.tagName || "").toUpperCase();

    if (tag === "INPUT" || tag === "TEXTAREA") {
      if (el.value === plain) return true;
      scheduleWrite(el, plain, html);
      return true;
    } else {
      const useInnerHTML = !!(html && html !== plain);
      const desired = useInnerHTML ? html : plain;
      if (useInnerHTML) {
        if (el.innerHTML === desired) return true;
      } else {
        if (el.textContent === desired) return true;
      }
      scheduleWrite(el, plain, html);
      return true;
    }
  }

  // Genera una chiave rappresentativa dello snapshot per confronto rapido
  function getSnapshotKey() {
    return safeRun(() => {
      const s = window.__GLB_COLOR_STATE_SNAPSHOT;
      if (!s) return null;
      if (opts.snapshotCompareByTs && s.ts) return String(s.ts);
      if (Array.isArray(s.selections)) {
        const arr = new Array(s.selections.length);
        for (let i = 0; i < s.selections.length; i++) {
          const sel = s.selections[i];
          const c = sel && sel.color ? sel.color : {};
          arr[i] = { label: c.label || c.colorName || c.name || "", hex: c.hex || c.colorHex || "" };
        }
        return JSON.stringify(arr);
      }
      return null;
    }, null);
  }

  // Avvia watcher che controlla periodicamente lo snapshot globale e chiama update se cambia
  function startSnapshotWatcher() {
    stopSnapshotWatcher();
    if (!(opts.snapshotPollInterval > 0)) return;
    lastSnapshotKey = getSnapshotKey();
    snapshotTimer = setInterval(() => {
      const key = getSnapshotKey();
      if (key && key !== lastSnapshotKey) {
        lastSnapshotKey = key;
        update();
      }
    }, opts.snapshotPollInterval);
  }

  // Ferma il watcher dello snapshot
  function stopSnapshotWatcher() {
    if (snapshotTimer) {
      clearInterval(snapshotTimer);
      snapshotTimer = null;
      lastSnapshotKey = null;
    }
  }

  // Pulisce l'observer delle mutazioni se presente
  function cleanupMutationObserver() {
    if (mutationObserver) {
      mutationObserver.disconnect();
      mutationObserver = null;
    }
  }

  // Installa un MutationObserver sul target per riapplicare il contenuto se viene modificato
  function installMutationReapply() {
    cleanupMutationObserver();
    if (!opts.reapplyOnMutation || !targetEl) return;
    reapplyCount = 0;
    let timer = null;

    mutationObserver = new MutationObserver(() => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        const colors = resolveColors();
        const desiredPlain = formatPlain(colors);
        const desiredHTML = formatHTML(colors);
        const currentText = (targetEl.tagName === "INPUT" || targetEl.tagName === "TEXTAREA") ? (targetEl.value || "") : (targetEl.textContent || "");
        const stripped = stripHtmlToText(desiredHTML);
        if (currentText !== desiredPlain && currentText !== stripped) {
          if (reapplyCount < (opts.reapplyAttempts || 3)) {
            reapplyCount++;
            writeToTarget(targetEl, desiredPlain, desiredHTML);
          }
        }
      }, opts.reapplyDebounceMs || 80);
    });

    mutationObserver.observe(targetEl, { childList: true, subtree: true, characterData: true });
  }

  let pollingActive = false;

  // Avvia il polling per cercare snapshot o target disponibili
  function startPolling() {
    stopPolling();
    if (!(opts.pollInterval > 0)) return;
    pollingActive = true;
    pollCount = 0;
    let attemptDelay = Math.max(opts.pollInitialDelayMs || 200, 50);
    const base = Math.max(1.5, opts.pollBackoffBase || 2);
    const maxDelay = Math.max(500, opts.pollBackoffMaxMs || 5000);

    function loop() {
      if (!pollingActive) return;
      pollCount++;
      update();
      const snap = readSnapshotSelections();
      if (Array.isArray(snap) && snap.length) { stopPolling(); return; }
      if (pollCount >= (opts.maxPollAttempts || 50)) { stopPolling(); return; }
      attemptDelay = !targetEl ? Math.min(maxDelay, Math.round(attemptDelay * base)) : opts.pollInterval;
      pollTimer = setTimeout(loop, attemptDelay);
    }

    pollTimer = setTimeout(loop, attemptDelay);
  }

  // Ferma il polling
  function stopPolling() {
    pollingActive = false;
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = null;
      pollCount = 0;
    }
  }

  // Funzione principale che legge i colori e scrive sul target
  function update() {
    if (opts.preferSnapshot) {
      const snap = readSnapshotSelections();
      if (!Array.isArray(snap) || snap.length === 0) {
        const globalSnap = window.__GLB_COLOR_STATE_SNAPSHOT;
        if (!globalSnap || !Array.isArray(globalSnap.selections)) return { written: false, count: 0 };
      }
    }

    const colors = resolveColors();
    const plain = formatPlain(colors);
    const html = formatHTML(colors);

    if (!targetEl && opts.target) {
      targetEl = (typeof opts.target === "string") ? document.querySelector(opts.target) : (opts.target instanceof Element ? opts.target : null);
    }

    if (!targetEl) return { written: false, count: colors.length };

    const wrote = writeToTarget(targetEl, plain, html);

    if (wrote && changeCallbacks.length) {
      for (let i = 0; i < changeCallbacks.length; i++) {
        try { changeCallbacks[i](plain); } catch (e) {}
      }
    }

    return { written: !!wrote, count: colors.length };
  }

  // Inizializzazione pubblica: configura opzioni, target e avvia watcher/polling
  ROOT.init = function (target, userOpts) {
    opts = Object.assign({}, DEFAULTS, userOpts || {});
    opts.locale = opts.locale || (userOpts && userOpts.locale) || (document.documentElement && document.documentElement.lang) || (window.Shopify && (Shopify.locale || Shopify.shop_locale)) || navigator.language || "it";
    opts.target = target || opts.target;
    targetEl = (typeof opts.target === "string") ? document.querySelector(opts.target) : (opts.target instanceof Element ? opts.target : null);

    update();

    if (opts.pollInterval && opts.pollInterval > 0) startPolling();

    if (opts.reapplyOnMutation) {
      if (targetEl) installMutationReapply();
      else {
        let attempts = 0;
        const maxAttempts = Math.max(10, Math.floor((opts.maxPollAttempts || 50) * 0.5));
        const checkInterval = Math.max(100, Math.min(500, opts.pollInterval || 200));
        const check = setInterval(() => {
          attempts++;
          targetEl = (typeof opts.target === "string") ? document.querySelector(opts.target) : (opts.target instanceof Element ? opts.target : null);
          if (targetEl) { clearInterval(check); installMutationReapply(); }
          else if (attempts >= maxAttempts) clearInterval(check);
        }, checkInterval);
      }
    }

    startSnapshotWatcher();

    return {
      update,
      start: startPolling,
      stop: stopPolling,
      getOptions: () => Object.assign({}, opts),
      onChange: cb => {
        if (typeof cb === "function") changeCallbacks.push(cb);
        return () => { changeCallbacks = changeCallbacks.filter(f => f !== cb); };
      }
    };
  };

  // Espone update e onChange anche come metodi diretti su ROOT
  ROOT.update = update;

  ROOT.onChange = cb => {
    if (typeof cb === "function") changeCallbacks.push(cb);
    return () => { changeCallbacks = changeCallbacks.filter(f => f !== cb); };
  };

  // Pulizia completa: ferma polling, observer, watcher e rimuove callback
  ROOT.cleanup = function () {
    stopPolling();
    cleanupMutationObserver();
    stopSnapshotWatcher();
    changeCallbacks = [];
    try { window.GLBColorSelect = null; } catch (e) {}
  };

  // Auto-inizializzazione per nodi con attributo data-glb-color-target
  function autoInitFromData() {
    const nodes = document.querySelectorAll("[data-glb-color-target]");
    if (!nodes || !nodes.length) return;
    for (let i = 0; i < nodes.length; i++) {
      const n = nodes[i];
      const selector = n.getAttribute("data-glb-color-target");
      const target = selector === "self" ? n : selector;
      const locale = n.getAttribute("data-glb-color-locale") || null;
      ROOT.init(target, { locale });
    }
  }

  if (document.readyState === "complete" || document.readyState === "interactive") setTimeout(autoInitFromData, 0);
  else document.addEventListener("DOMContentLoaded", autoInitFromData, { once: true });

})();
